import React from 'react';

import { fontStyles } from '~src/theme/fontStyles';
import { LogoInlineWrapper } from '~src/ui/logo-inline-wrapper';

import { LogoDeck } from '../commons/components/logo-deck-row';
import { useGlobalState, useSessionState } from '../global-state';
import { CompoundPriceComponent } from '../price/price';
import { colors } from '../theme/colors';
import { FreeShippingText } from './free-shipping-text';
import { ProgressBar } from './progress-bar';

interface FreeShippingIndicatorLayoutProps {
  logo: React.ReactNode;
  price: React.ReactNode;
  progressBar: React.ReactNode;
  text: React.ReactNode;
}

const FreeShippingIndicatorLayout: React.FC<FreeShippingIndicatorLayoutProps> = ({
  logo,
  price,
  progressBar,
  text,
}) => {
  return (
    <div data-test="free-shipping-indicator" css={styles.wrapper}>
      <div css={styles.textRow(!!logo)}>
        {text}
        {price}
        {logo && <LogoInlineWrapper>{logo}</LogoInlineWrapper>}
      </div>
      {progressBar}
    </div>
  );
};

export const FreeShippingIndicator: React.FC = () => {
  const globalState = useGlobalState();
  const session = useSessionState();

  const { next_level_price } = globalState;

  if (!session.flags.enable_free_shipping_indicator || !next_level_price) {
    return null;
  }

  const requiredValue = next_level_price.requirements!.cart_value ?? 0;
  const percent = (100 * session.cart.total_value) / requiredValue;

  const missingValue = next_level_price!.requirements!.cart_value! - session.cart.total_value;

  if (missingValue <= 0) {
    /* safety check */
    return null;
  }

  const logo = session.flags.enable_free_shipping_indicator_carrier_logo ? (
    <LogoDeck shippingMethods={[next_level_price.shipping_option!.shipping_method!]} />
  ) : undefined;

  return (
    <FreeShippingIndicatorLayout
      logo={logo}
      price={
        <CompoundPriceComponent
          basePrice={next_level_price.base_price!}
          price={next_level_price.next_price!}
        />
      }
      progressBar={<ProgressBar percent={percent} />}
      text={<FreeShippingText value={missingValue} />}
    />
  );
};

const styles = {
  wrapper: {
    borderTop: `1px solid ${colors.neutral90}`,
    padding: '24px 0',
  },
  textRow: (hasLogo: boolean) => ({
    marginBottom: '15px',
    marginTop: '-1px',
    display: 'grid',
    gridTemplateColumns: `1fr auto ${hasLogo ? '32px' : ''}`,
    columnGap: '10px',
    ...fontStyles.px14_20,
  }),
};
